// Compile-time "preview mode" for portal-ui.
//
// Lets a preview build open WITHOUT a login and land directly on the changed
// page, using a mock session + sample data only — never real employee or
// customer data, never secrets. Enabled ONLY when the build sets
// REACT_APP_PREVIEW_MODE=true (the throwaway portal-ui-preview build). The
// production CI build leaves it unset, so every guard below is statically
// false and compiles out (CRA inlines REACT_APP_* at build time).
//
// This deliberately bypasses the auth gate, so it MUST stay preview-only.
// Two things keep that safe: (1) the flag is a build-time env var prod never
// sets, and (2) every BFF call is short-circuited to a local fixture (see
// previewFixtures.js), so the mock token never authenticates against a real
// backend and no real data ever flows.

import { BACKEND_URL } from "../../config";
import { getFixture } from "./previewFixtures";

export const PREVIEW_MODE = process.env.REACT_APP_PREVIEW_MODE === "true";

// Obviously-fake credential. Never accepted by the real BFF; only present so
// ProtectedRoute (which gates purely on the presence of a token) renders the
// logged-in shell instead of bouncing to the login page.
export const MOCK_TOKEN = "PREVIEW_MODE_MOCK_TOKEN_NOT_A_REAL_JWT";

// Minimal, fictitious employee record. Shape mirrors what /login and
// get-user-info return (a flat Airtable field map). UI shell only — no real
// person, no PII. Tune the position list per feature if a page needs a role.
export const MOCK_USER_INFO = {
  Name: "Preview User",
  "Employee ID": "PREVIEW-0001",
  "Pestroutes ID": "0",
  "Position (from Position)": ["Sales Rep"],
  "Position (TEXT)": "Sales Rep",
  recID: "recPREVIEW0001",
  "Balance Points": 0,
  email: "preview@example.com",
};

// Which workflow the preview build opens straight into (feat #73 — the cost
// badge lives on the Workflow Preview screen, so land directly on a sample
// workflow rather than the list). Matched by the workflow fixtures in
// previewFixtures.js. No-op in prod (PREVIEW_MODE is statically false there).
export const PREVIEW_WORKFLOW_ID = "wf-preview-73";
// Seed for the content slice — set per preview build to the surface under review
// via REACT_APP_PREVIEW_CONTENT_STATE (so each feature's tagged preview deep-links
// to its own page without editing this default and colliding with siblings).
// No-op in prod (the env var is unset there). Falls back to the prior default.
export const PREVIEW_CONTENT_STATE =
  process.env.REACT_APP_PREVIEW_CONTENT_STATE || "Tournaments — View";

export function previewContentState() {
  return {
    state: PREVIEW_CONTENT_STATE,
    previousState: "",
    subState: {},
    deepLink: null,
  };
}

// Seed for the auth slice so the app boots already "logged in".
export function previewAuthState() {
  return {
    loading: false,
    userInfo: MOCK_USER_INFO,
    error: null,
    token: MOCK_TOKEN,
    isHydrated: true,
    detailsLoading: false,
    detailsError: null,
    detailsData: null,
    kindredPoints: 0,
    userInfoLastFetched: Date.now(),
    viewerToken: null,
    viewerInfo: null,
    isImpersonating: false,
    impersonationTarget: null,
  };
}

function isBackendUrl(url) {
  return typeof url === "string" && url.indexOf(BACKEND_URL) === 0;
}

// --- Network short-circuit -------------------------------------------------
// RTK Query (fetchBaseQuery) uses the global fetch; the axios `instance` uses
// XHR. Neutralize BOTH so no BFF call ever hangs (the cause of the "spins
// forever after login" preview failure) and no real data is fetched. Only
// BFF URLs are intercepted — same-origin asset and JS-chunk loads (relative
// URLs) fall through to the real network untouched.

// 1) Global fetch — covers all RTK Query services.
export function installPreviewFetch() {
  if (!PREVIEW_MODE || typeof window === "undefined" || !window.fetch) return;
  const realFetch = window.fetch.bind(window);
  window.fetch = (input, init) => {
    const url = typeof input === "string" ? input : input && input.url;
    if (!isBackendUrl(url)) return realFetch(input, init);
    const body = getFixture(url);
    return Promise.resolve(
      new Response(JSON.stringify(body), {
        status: 200,
        headers: { "Content-Type": "application/json" },
      })
    );
  };
}

// 2) axios instance adapter — covers thunks (silentRefetchUserInfo, login,
//    sales actions). The get-user-info fixture keeps the session alive
//    instead of tripping App.js's session-expiry -> logout path.
export function installPreviewAxios(instance) {
  if (!PREVIEW_MODE || !instance) return;
  instance.defaults.adapter = (config) => {
    const url = `${config.baseURL || ""}${config.url || ""}`;
    return Promise.resolve({
      data: getFixture(url),
      status: 200,
      statusText: "OK",
      headers: {},
      config,
      request: {},
    });
  };
}
